import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Trash2, Save } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const http = axios.create({ withCredentials: true });

export default function BarbersEditor() {
  const [items, setItems] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () =>
    http.get(`${API}/admin/barbers`)
      .then((r) => setItems(r.data))
      .catch(() => toast.error("Erro ao carregar barbeiros."));

  useEffect(() => { load(); }, []);

  const update = (i, field, value) =>
    setItems((prev) => prev.map((b, idx) => (idx === i ? { ...b, [field]: value } : b)));

  const add = () =>
    setItems((prev) => [...prev, { id: null, name: "", role: "Barbeiro", tag: "", phone: "", active: true }]);

  const remove = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));

  const uploadPhoto = async (barberId, file) => {
    const fd = new FormData();
    fd.append("file", file);
    try {
      await http.post(`${API}/admin/barbers/${barberId}/photo`, fd);
      toast.success("Foto do barbeiro atualizada no site.");
      load();
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro no upload da foto.");
    }
  };

  const save = async () => {
    if (items.some((b) => b.name.trim().length < 2)) {
      toast.error("Todo barbeiro precisa de um nome.");
      return;
    }
    setSaving(true);
    try {
      const { data } = await http.put(`${API}/admin/barbers`, { items });
      setItems(data);
      toast.success("Barbeiros salvos. O site já mostra a nova equipe.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Erro ao salvar.");
    } finally {
      setSaving(false);
    }
  };

  if (!items) {
    return <p className="py-16 text-center text-sm text-smoke" data-testid="barbers-loading">Carregando...</p>;
  }

  return (
    <main className="max-w-6xl mx-auto px-5 md:px-8 py-10" data-testid="barbers-editor">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="font-bebas text-4xl tracking-wide text-cream">Equipe de barbeiros</h2>
          <p className="text-sm text-smoke mt-1">Barbeiros inativos não aparecem no agendamento. O histórico de agendamentos é mantido.</p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={add}
            data-testid="barbers-add-button"
            className="focus-gold inline-flex items-center gap-2 border border-gold text-gold font-bebas text-lg tracking-widest px-5 py-2.5 hover:bg-gold hover:text-ink transition-colors"
          >
            <Plus className="w-4 h-4" /> Novo barbeiro
          </button>
          <button
            onClick={save}
            disabled={saving}
            data-testid="barbers-save-button"
            className="focus-gold inline-flex items-center gap-2 bg-gold text-ink font-bebas text-lg tracking-widest px-6 py-2.5 hover:bg-golddark transition-colors disabled:opacity-40"
          >
            <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>

      <div className="mt-8 space-y-3">
        {items.map((b, i) => (
          <div
            key={i}
            data-testid={`barber-row-${i}`}
            className={`grid grid-cols-2 md:grid-cols-[88px_1fr_150px_150px_1fr_100px_48px] gap-3 items-center border p-4 transition-colors ${b.active ? "border-white/10 bg-surface" : "border-white/5 bg-surface/40 opacity-60"}`}
          >
            <div className="flex flex-col items-center gap-2">
              <div className="w-14 h-14 rounded-full border border-gold/50 overflow-hidden flex items-center justify-center bg-ink">
                {b.photo ? (
                  <img src={`${process.env.REACT_APP_BACKEND_URL}/api/barbers/${b.id}/photo`} alt={b.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="font-bebas text-2xl text-gold">{b.name ? b.name[0].toUpperCase() : "?"}</span>
                )}
              </div>
              {b.id ? (
                <label className="cursor-pointer text-[10px] uppercase tracking-widest text-gold hover:text-golddark transition-colors" data-testid={`barber-photo-${i}`}>
                  Foto
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    className="hidden"
                    aria-label={`Foto do barbeiro ${i + 1}`}
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) uploadPhoto(b.id, f);
                      e.target.value = "";
                    }}
                  />
                </label>
              ) : (
                <span className="text-[10px] uppercase tracking-widest text-smoke">Salve 1º</span>
              )}
            </div>
            <input
              type="text"
              value={b.name}
              onChange={(e) => update(i, "name", e.target.value)}
              placeholder="Nome"
              aria-label={`Nome do barbeiro ${i + 1}`}
              data-testid={`barber-name-${i}`}
              className="focus-gold bg-ink border border-white/15 text-cream font-manrope px-4 py-2.5 text-sm outline-none"
            />
            <input
              type="text"
              value={b.role}
              onChange={(e) => update(i, "role", e.target.value)}
              placeholder="Cargo (ex.: Barbeiro & Fundador)"
              aria-label={`Cargo do barbeiro ${i + 1}`}
              data-testid={`barber-role-${i}`}
              className="focus-gold bg-ink border border-white/15 text-cream font-manrope px-4 py-2.5 text-sm outline-none"
            />
            <input
              type="tel"
              value={b.phone || ""}
              onChange={(e) => update(i, "phone", e.target.value)}
              placeholder="WhatsApp (ex.: 47999998888)"
              aria-label={`WhatsApp do barbeiro ${i + 1}`}
              data-testid={`barber-phone-${i}`}
              className="focus-gold bg-ink border border-white/15 text-cream font-manrope px-4 py-2.5 text-sm outline-none"
            />
            <input
              type="text"
              value={b.tag}
              onChange={(e) => update(i, "tag", e.target.value)}
              placeholder="Especialidade (ex.: Degradê e navalha)"
              aria-label={`Especialidade do barbeiro ${i + 1}`}
              data-testid={`barber-tag-${i}`}
              className="focus-gold col-span-2 md:col-span-1 bg-ink border border-white/15 text-cream font-manrope px-4 py-2.5 text-sm outline-none"
            />
            <button
              onClick={() => update(i, "active", !b.active)}
              data-testid={`barber-active-${i}`}
              className={`focus-gold font-bebas tracking-widest text-sm px-3 py-2.5 border transition-colors ${b.active ? "border-eragreen text-green-400" : "border-white/15 text-smoke"}`}
            >
              {b.active ? "Ativo" : "Inativo"}
            </button>
            <button
              onClick={() => remove(i)}
              aria-label={`Remover barbeiro ${i + 1}`}
              data-testid={`barber-remove-${i}`}
              className="focus-gold p-2.5 border border-white/15 text-smoke hover:border-red-500 hover:text-red-400 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <p className="mt-6 text-xs text-smoke">Lembre-se de clicar em <span className="text-gold">Salvar</span> para aplicar as mudanças no site.</p>
    </main>
  );
}
